import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, X, ChevronLeft, ChevronRight } from 'lucide-react';
import type { GuestHostUpdate } from '@/types/database';

interface HostUpdatesSectionProps {
  updates: GuestHostUpdate[];
  darkMode: boolean;
}

export function HostUpdatesSection({ updates, darkMode }: HostUpdatesSectionProps) {
  const { t } = useTranslation();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    if (lightboxIndex !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [lightboxIndex]);

  if (!updates || updates.length === 0) return null;

  const getTimeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return t('hostUpdates.timeAgo.daysAgo', { count: days });
    if (hours > 0) return t('hostUpdates.timeAgo.hoursAgo', { count: hours });
    if (minutes > 0) return t('hostUpdates.timeAgo.minutesAgo', { count: minutes });
    return t('hostUpdates.timeAgo.justNow');
  };

  return (
    <>
      {/* Stories-style section */}
      <div
        className={`rounded-2xl p-4 ${
          darkMode
            ? 'bg-slate-800 border border-slate-700 shadow-xl shadow-black/40 ring-1 ring-white/5'
            : 'bg-white border border-cyan-100 shadow-lg shadow-gray-200/80'
        }`}
      >
        {/* Header - consistent with other sections */}
        <div className="flex items-center gap-2.5 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className={`font-bold text-lg tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('hostUpdates.guest.moments')}
              </h3>
              {updates.length > 1 && (
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                  darkMode ? 'bg-cyan-500/20 text-cyan-300' : 'bg-cyan-100 text-cyan-700'
                }`}>
                  {updates.length}
                </span>
              )}
              <div className={`flex-1 h-px ${darkMode ? 'bg-slate-700/50' : 'bg-gray-200'}`} />
            </div>
            <p className={`text-[11px] mt-0.5 ${darkMode ? 'text-cyan-400/70' : 'text-cyan-600/70'}`}>
              {t('hostUpdates.guest.newsFromHost')}
            </p>
          </div>
        </div>

        {/* Stories circles carousel */}
        <div className="flex gap-3 overflow-x-auto pb-1 snap-x snap-mandatory scrollbar-hide">
          {updates.map((update, index) => (
            <button
              key={update.id}
              onClick={() => setLightboxIndex(index)}
              className="flex-shrink-0 flex flex-col items-center gap-1.5 snap-start group"
            >
              {/* Circle with gradient ring (Instagram stories style) */}
              <div className={`p-[2.5px] rounded-full bg-gradient-to-br ${
                darkMode
                  ? 'from-cyan-400 via-blue-500 to-cyan-500'
                  : 'from-cyan-500 via-blue-500 to-cyan-500'
              }`}>
                <div className={`w-16 h-16 rounded-full overflow-hidden border-2 ${
                  darkMode ? 'border-slate-900' : 'border-white'
                }`}>
                  <img
                    src={update.image_url}
                    alt={update.caption || ''}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
              </div>
              {/* Time label */}
              <span className={`text-[10px] font-medium max-w-[72px] truncate ${
                darkMode ? 'text-slate-400' : 'text-gray-500'
              }`}>
                {getTimeAgo(update.created_at)}
              </span>
            </button>
          ))}

          {/* "See all" hint if many */}
          {updates.length > 4 && (
            <div className="flex-shrink-0 flex flex-col items-center justify-center gap-1.5 snap-start">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center border-2 border-dashed ${
                darkMode ? 'border-slate-600 text-slate-400' : 'border-gray-300 text-gray-400'
              }`}>
                <Camera className="w-5 h-5" />
              </div>
              <span className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                {t('hostUpdates.guest.more')}
              </span>
            </div>
          )}
        </div>

        {/* Preview caption of first update */}
        {updates[0]?.caption && (
          <p className={`mt-2.5 text-xs italic line-clamp-1 ${
            darkMode ? 'text-slate-400' : 'text-gray-500'
          }`}>
            &ldquo;{updates[0].caption}&rdquo;
          </p>
        )}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxIndex(null)}
        >
          {/* Close button */}
          <button
            onClick={() => setLightboxIndex(null)}
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Counter */}
          <div className="absolute top-4 left-4 z-10 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm">
            {lightboxIndex + 1} / {updates.length}
          </div>

          {/* Navigation arrows */}
          {updates.length > 1 && (
            <>
              {lightboxIndex > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxIndex(lightboxIndex - 1);
                  }}
                  className="absolute left-3 z-10 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}
              {lightboxIndex < updates.length - 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxIndex(lightboxIndex + 1);
                  }}
                  className="absolute right-3 z-10 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}
            </>
          )}

          {/* Image + Caption */}
          <div
            className="max-w-[90vw] max-h-[85vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={updates[lightboxIndex].image_url}
              alt={updates[lightboxIndex].caption || ''}
              className="max-w-full max-h-[75vh] object-contain rounded-lg"
            />
            {updates[lightboxIndex].caption && (
              <p className="mt-3 text-white/80 text-sm text-center max-w-md px-4">
                {updates[lightboxIndex].caption}
              </p>
            )}
            <p className="mt-1 text-white/40 text-xs">
              {getTimeAgo(updates[lightboxIndex].created_at)}
            </p>
          </div>
        </div>
      )}
    </>
  );
}
